import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  viewChildren,
} from '@angular/core';
import { FieldArrayType } from '@ngx-formly/core';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, takeUntil } from 'rxjs/operators';

import { ProductContextDirective } from 'ish-core/directives/product-context.directive';
import { ProductView } from 'ish-core/models/product-view/product-view.model';

/**
 * The Quick Order Repeat Component provides a formly field element, which displays a field array of n elements depending on the according model.
 * Each line displays a form field for the sku and for the according quantity.
 * The component handles adding and removing of elements to the according formly model.
 * The component controls the product contexts for a proper behavior between entered product sku and its quantity field.
 */
@Component({
  selector: 'custom-quickorder-flyout-form',
  templateUrl: './custom-quickorder-flyout-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CustomQuickorderFlyoutFormComponent extends FieldArrayType implements AfterViewInit, OnDestroy {
  readonly contexts = viewChildren(ProductContextDirective);
  private destroy$ = new Subject<void>();

  constructor(private cdRef: ChangeDetectorRef) {
    super();
  }

  ngAfterViewInit() {
    this.updateContexts();
  }
  /**
   * Set the form control field to the according product context and handle its behavior.
   */
  updateContexts() {
    this.cdRef.detectChanges();
    this.contexts().forEach((directive, index) => {
      const field = this.field.fieldGroup[index].fieldGroup[0];
      const formControl = field.formControl;

      directive.getContext().connect('sku', formControl.valueChanges.pipe(debounceTime(500)));
      formControl.valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$)).subscribe(value => {
        if (!value) {
          const failedProduct: ProductView = {
            defaultCategory: undefined,
            failed: false,
            sku: '',
          };
          const addingEmptyProduct = () => failedProduct;
          directive.getContext().set('product', addingEmptyProduct);
        }
      });
      formControl.setAsyncValidators(() =>
        directive
          .getContext()
          .select('product')
          .pipe(
            map(product => {
              this.cdRef.markForCheck();

              const trimmedValue = formControl.value?.trim();
              if (!trimmedValue) {
                return;
              }
              const flyoutSkus = [...this.model];
              const isExistingProduct = flyoutSkus.some(
                (skuItem, arrIndex) => arrIndex !== index && skuItem.sku === product.sku
              );
              if (isExistingProduct) {
                directive.getContext().set('productIndex', () => index);
              } else {
                directive.getContext().set('productIndex', () => undefined);
              }
              if (!product.available) {
                return { validProduct: false };
              }
              return product.failed && formControl.value.trim() !== ''
                ? { validProduct: false }
                : isExistingProduct
                ? { existingProduct: false }
                : undefined;
            })
          )
      );
    });
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
